// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { ErrorInformationStorage } from '@shared/commons/ErrorInformationStorage';
import { Point, Rectangle } from '@shared/commons/Geometry';
import { Cursors } from '@shared/commons/Cursors';
import { TerceraChartBaseRenderer } from './TerceraChartBaseRenderer';
import { MouseButtons } from '@front/controls/UtilsClasses/ControlsUtils';
import { TerceraChartAction, TerceraChartActionEnum } from '../TerceraChartAction';
import { TerceraChartPriceScaleRendererMouseState } from '../Renderers/Scales/TerceraChartPriceScaleRenderer';
import { ThemeManager } from '@front/controls/misc/ThemeManager';
import { PlateOverlay } from '../../Chart/Tools/PlateOverlay';
import { Pen, SolidBrush } from '@shared/commons/Graphics';
import { Resources } from '@shared/localizations/Resources';
import { LayersEnum } from '../Utils/LayersEnum';
import { NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
export class TerceraChartPlateRenderer extends TerceraChartBaseRenderer {
    constructor(chart) {
        super(chart);
        this.WinNum = -1;
        this.ellipsisRectangle = new Rectangle();
        this.totalWidth = 0;
        this.lastMouseMovePt = Point.Empty();
        this.mouseState = TerceraChartPriceScaleRendererMouseState.None;
        this.plateRenderer = true;
        this.platesIndicator = [];
        this.platesOverlay = [];
        this.plates = [];
        this.mouseDownButtonRect = new Rectangle();
        this._showPlates = true;
        this.toggleButtonRect = new Rectangle();
        this.toggleButtonHovered = false;
        this.ellipsisHovered = false;
        this.platesSizeInMainWindow = 20;
        this.platesSizeInIndicatorWindow = 20;
        this.paddingX = 8;
        this.gap = 8;
        this.assignLayer = LayersEnum.CrossHair;
        this.SetClassName('TerceraChartPlateRenderer');
        this.ThemeChanged();
    }
    get ShowPlates() {
        return this._showPlates;
    }
    set ShowPlates(value) {
        if (this._showPlates !== value) {
            this._showPlates = value;
            this.UpdatePlatesVisibility();
        }
    }
    get Width() {
        return this.Rectangle.Width - this.paddingX * 2;
    }
    GetPlateOverlay(overlay, createIfNotExist = true) {
        const index = this.findOverlayIndex(overlay);
        if (index === -1) {
            if (createIfNotExist) {
                return new PlateOverlay(overlay);
            }
            else {
                return null;
            }
        }
        return this.platesOverlay[index];
    }
    findOverlayIndex(overlay) {
        for (let i = 0; i < this.platesOverlay.length; i++) {
            if (this.platesOverlay[i].terceraChartOverlay === overlay) {
                return i;
            }
        }
        return -1;
    }
    GetPlateIndicator(renderer, createIfNotExist = true) {
        const index = this.findIndicatorIndex(renderer);
        if (index === -1) {
            if (createIfNotExist && renderer.Indicator !== null) {
                return renderer.PlateFactory();
            }
            else {
                return null;
            }
        }
        return this.platesIndicator[index];
    }
    findIndicatorIndex(renderer) {
        for (let i = 0; i < this.platesIndicator.length; i++) {
            if (this.platesIndicator[i].Renderer === renderer) {
                return i;
            }
        }
        return -1;
    }
    Clear() {
        for (let i = 0; i < this.platesIndicator.length; i++) {
            this.platesIndicator[i].Dispose();
        }
        this.platesIndicator = [];
        for (let i = 0; i < this.platesOverlay.length; i++) {
            this.platesOverlay[i].Dispose();
        }
        this.platesOverlay = [];
        this.UpdatePlates();
    }
    UpdatePlateIndicators(plates) {
        this.platesIndicator = plates;
        this.UpdatePlates();
    }
    UpdatePlateOverlays(plates) {
        this.platesOverlay = plates;
        this.UpdatePlates();
    }
    UpdatePlates() {
        this.plates = [].concat(this.platesOverlay);
        this.plates = this.plates.concat(this.platesIndicator);
    }
    ResetPlatesSize() {
        for (let i = 0; i < this.plates.length; i++) {
            this.plates[i].ResetPlateSize();
        }
    }
    Draw(gr, window, windowsContainer, advParams = null) {
        const param = advParams;
        const cashItemSeries = param.mainPriceRenderer.Series;
        if (isNullOrUndefined(cashItemSeries) || this.IsEmpty()) {
            return;
        }
        this.AnalyzeWidth(gr, window, cashItemSeries);
        gr.save();
        if (!window.IsMainWindow) {
            const path = new Path2D();
            const borderW = window.subHeaderBorderWidth;
            const headerRect = window.windowHeaderRectangle;
            path.roundRect(headerRect.X + borderW, headerRect.Y + borderW, headerRect.Width - borderW * 2, headerRect.Height - borderW * 2, 4);
            gr.clip(path);
        }
        const showMoreShown = !this.ellipsisRectangle.IsEmpty();
        for (const element of this.plates) {
            try {
                const isOverlay = element.isOverlay;
                const curCashItemSeries = isOverlay
                    ? element.terceraChartOverlay.mainPriceRenderer.Series
                    : cashItemSeries;
                if (element.PlateVisible) {
                    element.Draw(gr, window, curCashItemSeries, this.lastMouseMovePt);
                }
            }
            catch (ex) {
                ErrorInformationStorage.GetException(ex);
            }
        }
        if (showMoreShown) {
            this.DrawMoreButton(gr, window);
        }
        this.DrawToggleButton(gr, window);
        gr.restore();
    }
    AnalyzeWidth(gr, window, cashItemSeries) {
        var _a;
        const curX0 = window.IsMainWindow ? this.Rectangle.X + this.paddingX : this.Rectangle.X;
        let curX = curX0;
        let plateSize, gap, budgetRightX;
        if (window.IsMainWindow) {
            plateSize = this.platesSizeInMainWindow;
            gap = this.gap;
            budgetRightX = this.Rectangle.Right() - plateSize * 2 - gap - this.paddingX;
        }
        else {
            plateSize = this.platesSizeInIndicatorWindow;
            gap = 0;
            budgetRightX = this.Rectangle.Right() - plateSize * 3 - gap - this.paddingX;
        }
        const paddingY = (this.Rectangle.Height - plateSize) / 2;
        let totalWidth = 0;
        for (let i = 0; i < this.plates.length; i++) {
            const plate = this.plates[i];
            plate.clientRectangle = new Rectangle(curX, this.Rectangle.Y + paddingY, plate.Width, this.Rectangle.Height - paddingY * 2);
            const curCashItemSeries = plate.isOverlay
                ? this.plates[i].terceraChartOverlay.mainPriceRenderer.Series
                : cashItemSeries;
            plate.calculatePlateSizes(gr, window, curCashItemSeries, this.lastMouseMovePt);
            plate.clientRectangle.Width = plate.Width;
            totalWidth += plate.Width + gap;
            const plateRightX = curX + plate.Width + gap;
            if (plateRightX > budgetRightX || !this.ShowPlates) {
                plate.PlateVisible = false;
                plate.clientRectangle = Rectangle.Empty();
            }
            else {
                plate.PlateVisible = true;
                curX = plateRightX;
            }
        }
        this.totalWidth = totalWidth;
        const needShowMore = this.ShowPlates && !((_a = this.plates.at(-1)) === null || _a === void 0 ? void 0 : _a.PlateVisible);
        if (needShowMore) {
            const showMoreY = window.IsMainWindow ? this.Rectangle.Y + paddingY : this.Rectangle.Y;
            this.ellipsisRectangle = new Rectangle(curX, showMoreY, plateSize, plateSize);
            curX += this.ellipsisRectangle.Width;
        }
        else {
            this.ellipsisRectangle = Rectangle.Empty();
        }
        curX += gap;
        const toggleY = window.IsMainWindow ? this.Rectangle.Y + paddingY : this.Rectangle.Y;
        this.toggleButtonRect = new Rectangle(curX, toggleY, plateSize, plateSize);
    }
    CreateEllipsisMenuItems() {
        const items = [];
        for (let i = 0; i < this.plates.length; i++) {
            const plate = this.plates[i];
            if (plate.PlateVisible) {
                continue;
            }
            items.push({
                type: NewContextMenuItemType.Default,
                text: plate.getTextName(),
                subitems: [
                    {
                        type: NewContextMenuItemType.Default,
                        text: plate.getVisibilityText(),
                        event: (item) => {
                            this.ActionProcessVisible(plate);
                        }
                    },
                    {
                        type: NewContextMenuItemType.Default,
                        text: plate.getPropertiesText(),
                        event: (item) => {
                            this.ActionProcessSettings(plate);
                        }
                    },
                    {
                        type: NewContextMenuItemType.Default,
                        text: plate.getCloseText(),
                        event: (item) => {
                            this.ActionProcessRemove(plate);
                        }
                    }
                ]
            });
        }
        return items;
    }
    GetTooltip(e) {
        if (this.toggleButtonHovered) {
            const key = this.ShowPlates ? 'chart.HidePlatesBtn.Tooltip' : 'chart.ShowPlatesBtn.ToolTip';
            return Resources.getResource(key, [String(this.plates.length)]);
        }
        if (this.ellipsisHovered) {
            return '';
        }
        return super.GetTooltip(e);
    }
    GetCursor(e) {
        if (!this.Visible)
            return null;
        if (this.toggleButtonHovered || this.ellipsisHovered)
            return Cursors.Hand;
        return this.plates.some((plate) => plate.GetHover()) ? Cursors.Hand : null;
    }
    IsEmpty() {
        return this.platesIndicator.length === 0 && this.platesOverlay.length === 0;
    }
    GetContextMenu(e, chart) {
        if (!this.Visible) {
            return super.GetContextMenu(e, chart);
        }
        let hoverPlate;
        const plates = this.plates;
        for (let i = 0, len = plates.length; i < len; i++) {
            const plate = plates[i];
            if (plate.GetHover() === true) {
                hoverPlate = plate;
                break;
            }
        }
        if (isNullOrUndefined(hoverPlate)) {
            return super.GetContextMenu(e, chart);
        }
        const renderer = hoverPlate.Renderer;
        const overlay = hoverPlate.terceraChartOverlay;
        return hoverPlate.isIndicator === true
            ? [
                chart.TerceraChartActionProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.PlateIndicatorVisible, renderer)),
                chart.TerceraChartActionProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.PlateIndicatorDublicate, renderer)),
                chart.TerceraChartActionProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.PlateIndicatorRemove, renderer)),
                { type: NewContextMenuItemType.Separator },
                chart.TerceraChartActionProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.PlateIndicatorSettings, renderer))
            ]
            : [
                chart.TerceraChartActionProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.PlateOverlayVisible, overlay)),
                chart.TerceraChartActionProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.PlateOverlayRemove, overlay)),
                { type: NewContextMenuItemType.Separator },
                chart.TerceraChartActionProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.PlateOverlaySettings, overlay))
            ];
    }
    // Process mouse events
    ProcessMouseDown(e) {
        if (!this.Visible || e.Button !== MouseButtons.Left) {
            return false;
        }
        this.lastMouseMovePt = e.Location;
        this.mouseState = TerceraChartPriceScaleRendererMouseState.None;
        this.mouseDownButtonRect = Rectangle.Empty();
        /* Handle a click on the toggle "Hide indicator legend" button */
        if (this.toggleButtonRect.Contains(e.Location.X, e.Location.Y)) {
            this.ShowPlates = !this.ShowPlates;
            return true;
        }
        if (this.ellipsisRectangle.Contains(e.Location.X, e.Location.Y)) {
            showContextMenu({ x: e.ClientLocation.X, y: e.ClientLocation.Y, items: this.CreateEllipsisMenuItems() });
            e._baseEvent.stopPropagation();
            return true;
        }
        else if (this.Rectangle.Contains(e.Location.X, e.Location.Y)) {
            for (const plate of this.plates) {
                if (!plate.PlateVisible) {
                    continue;
                }
                if (plate.closeRectangle.Contains(e.Location.X, e.Location.Y)) {
                    this.mouseDownButtonRect = plate.closeRectangle;
                    return true;
                }
                else if (plate.visibilityRectangle.Contains(e.Location.X, e.Location.Y)) {
                    this.mouseDownButtonRect = plate.visibilityRectangle;
                    return true;
                }
                else if (plate.editRectangle.Contains(e.Location.X, e.Location.Y)) {
                    this.mouseDownButtonRect = plate.editRectangle;
                    return true;
                }
                else if (plate.ellipseRectangle.Contains(e.Location.X, e.Location.Y)) {
                    plate.SetSelected();
                    return true;
                }
            }
        }
        return false;
    }
    ProcessMouseMove(e) {
        if (!this.Visible) {
            return false;
        }
        this.toggleButtonHovered = this.toggleButtonRect.Contains(e.Location.X, e.Location.Y);
        this.ellipsisHovered = this.ellipsisRectangle.Contains(e.Location.X, e.Location.Y);
        if (this.ShowPlates) {
            for (const plate of this.plates) {
                const newSelection = plate.clientRectangle.Contains(e.Location.X, e.Location.Y);
                const oldSelection = plate.GetHover();
                if (oldSelection !== newSelection) {
                    plate.SetHover(newSelection);
                    e.NeedRedraw = LayersEnum.All; // надо также перерисовать все, что в фоне
                }
                if (oldSelection)
                    plate.ProcessMouseMove(e.Location.X, e.Location.Y);
                if (oldSelection && !newSelection)
                    plate.ProcessMouseLeave();
                if (newSelection)
                    e.NeedRedraw = LayersEnum.All;
            }
        }
        let res = false;
        if (this.mouseState === TerceraChartPriceScaleRendererMouseState.Moving) {
            res = true;
        }
        this.lastMouseMovePt = e.Location;
        if (!res) {
            return super.ProcessMouseMove(e);
        }
        else {
            return true;
        }
    }
    ProcessMouseUp(e) {
        if (this.mouseState === TerceraChartPriceScaleRendererMouseState.None &&
            this.mouseDownButtonRect.Contains(e.Location.X, e.Location.Y)) {
            this.mouseDownButtonRect = Rectangle.Empty();
            if (this.ShowPlates)
                for (let i = 0; i < this.plates.length; i++) {
                    if (this.plates[i].GetHover() === true) {
                        if (this.plates[i].closeRectangle.Contains(e.Location.X, e.Location.Y)) {
                            this.ActionProcessRemove(this.plates[i]);
                        }
                        else if (this.plates[i].editRectangle.Contains(e.Location.X, e.Location.Y)) {
                            this.ActionProcessSettings(this.plates[i]);
                        }
                        else if (this.plates[i].visibilityRectangle.Contains(e.Location.X, e.Location.Y)) {
                            this.ActionProcessVisible(this.plates[i]);
                        }
                        return true;
                    }
                }
        }
        this.mouseState = TerceraChartPriceScaleRendererMouseState.None;
        this.mouseDownButtonRect = Rectangle.Empty();
        super.ProcessMouseUp(e);
        return false;
    }
    ActionProcessRemove(plate) {
        if (plate.isIndicator === true)
            this.chart.RemoveIndicator(plate.Renderer, true);
        else if (plate.isOverlay === true)
            this.chart.RemoveOverlay(plate.terceraChartOverlay);
    }
    ActionProcessSettings(plate) {
        if (plate.isIndicator === true) {
            this.chart.TerceraChartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(TerceraChartActionEnum.PlateIndicatorSettings, plate.Renderer));
        }
        else if (plate.isOverlay === true) {
            this.chart.TerceraChartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(TerceraChartActionEnum.PlateOverlaySettings, plate.terceraChartOverlay));
        }
    }
    ActionProcessVisible(plate) {
        if (plate.isIndicator === true) {
            this.chart.TerceraChartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(TerceraChartActionEnum.PlateIndicatorVisible, plate.Renderer));
        }
        else if (plate.isOverlay === true) {
            this.chart.TerceraChartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(TerceraChartActionEnum.PlateOverlayVisible, plate.terceraChartOverlay));
        }
    }
    ProcessMouseLeave(e) {
        super.ProcessMouseEnter(e);
        for (let i = 0; i < this.plates.length; i++) {
            this.plates[i].SetHover(false);
        }
        this.lastMouseMovePt = Point.Empty();
    }
    DrawMoreButton(gr, window) {
        const { chartMoreIconDefault, chartMoreIconHovered } = ThemeManager.NewImages;
        const isHover = this.ellipsisHovered;
        const moreIcon = isHover ? chartMoreIconHovered : chartMoreIconDefault;
        let backBrush = null;
        let borderPen = null;
        const colors = ThemeManager.colors;
        if (isHover) {
            backBrush = new SolidBrush(colors.chartButtonBackgroundHovered);
            borderPen = window.IsMainWindow ? new Pen(colors.chartButtonBorderHovered) : null;
        }
        else {
            backBrush = new SolidBrush(colors.chartButtonBackground);
            borderPen = window.IsMainWindow ? new Pen(colors.chartButtonBorder) : null;
        }
        if (window.IsMainWindow) {
            gr.RoundRectangle(this.ellipsisRectangle, 4, backBrush, borderPen);
        }
        else {
            gr.FillRectWithRect(backBrush, this.ellipsisRectangle);
        }
        gr.DrawCenteredImage(moreIcon.image, this.ellipsisRectangle);
    }
    DrawToggleButton(gr, window) {
        let backBrush = null;
        let borderPen = null;
        let image = null;
        const colors = ThemeManager.colors;
        if (this.toggleButtonHovered) {
            backBrush = new SolidBrush(colors.chartButtonBackgroundHovered);
            borderPen = window.IsMainWindow ? new Pen(colors.chartButtonBorderHovered) : null;
            image = this.ShowPlates
                ? ThemeManager.NewImages.chartIndicatorPlatesCollapserArrowRightHovered.image
                : ThemeManager.NewImages.chartIndicatorPlatesCollapserArrowLeftHovered.image;
        }
        else {
            backBrush = new SolidBrush(colors.chartButtonBackground);
            borderPen = window.IsMainWindow ? new Pen(colors.chartButtonBorder) : null;
            image = this.ShowPlates
                ? ThemeManager.NewImages.chartIndicatorPlatesCollapserArrowRight.image
                : ThemeManager.NewImages.chartIndicatorPlatesCollapserArrowLeft.image;
        }
        if (window.IsMainWindow) {
            gr.RoundRectangle(this.toggleButtonRect, 4, backBrush, borderPen);
        }
        else {
            gr.FillRectWithRect(backBrush, this.toggleButtonRect);
        }
        gr.DrawCenteredImage(image, this.toggleButtonRect, 6, 11);
    }
    UpdatePlatesVisibility() {
        for (const plate of this.plates) {
            plate.PlateVisible = this.ShowPlates;
        }
        this.chart.IsDirty(LayersEnum.All);
    }
}
